import React, { useState, useEffect } from 'react';
import { PdfTemplate, pdfTemplatesApi } from '../api';
import { buildingsApi } from '../api/buildingsApi';
import { useLang } from '../contexts/LangContext';
import { LogoPathField } from './common/LogoPathField';
import { FeeNoticeRichTextarea } from './common/FeeNoticeRichTextarea';
import { toast } from 'react-toastify';

interface TemplateEditorProps {
  template: PdfTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  /** When set, save creates/updates a building override instead of editing operator template. */
  buildingContextId?: string | null;
  saveAsBuildingOverride?: boolean;
  /** Operator user id when superadmin edits operator-default templates (logo upload + create). */
  operatorScopeId?: string | null;
}

interface TemplateContent {
  layout: string;
  header: {
    showLogo: boolean;
    showBuildingInfo: boolean;
    title: string;
    titleEn?: string;
    bilingual: boolean;
    companyName?: string;
    companyNameEn?: string;
    logoPath?: string | null;
    buildingLogoPath?: string | null;
  };
  sections?: {
    hideZeroSections?: boolean;
  };
  customer: {
    showBox: boolean;
    fields: string[];
  };
  table: {
    headers: string[];
    showBorders: boolean;
    alternateRowColors: boolean;
    showVAT: boolean;
  };
  footer: {
    showPaymentInfo: boolean;
    showBankInfo: boolean;
    showSignature: boolean;
    showQRCode: boolean;
    showAppQrCode?: boolean;
    showNotice: boolean;
    showSignatures?: boolean;
    defaultReason?: string;
    hotline?: string;
    officeHours?: string;
    officeHoursEn?: string;
    noticeText?: string;
    noticeTextEn?: string;
  };
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

const DEFAULT_TEMPLATE_CONTENT: TemplateContent = {
  layout: 'professional',
  header: {
    showLogo: true,
    showBuildingInfo: true,
    title: 'BẢNG TÍNH PHÍ DỊCH VỤ',
    bilingual: true,
  },
  customer: {
    showBox: true,
    fields: ['name', 'address', 'contract', 'taxCode', 'phone'],
  },
  table: {
    headers: [
      'Khoản thu\n(Description)',
      'Chỉ số đầu/cuối\n(From/To)',
      'Đơn vị\n(Unit)',
      'Đơn giá\n(Unit Price)',
      'Thành tiền\n(Amount)',
      '%',
      'Thuế GTGT\n(Tax amount)',
      'Tổng cộng\n(Total)',
    ],
    showBorders: true,
    alternateRowColors: true,
    showVAT: true,
  },
  footer: {
    showPaymentInfo: true,
    showBankInfo: true,
    showSignature: true,
    showQRCode: true,
    showNotice: true,
  },
  colors: {
    primary: '#2563eb',
    secondary: '#64748b',
    accent: '#1e293b',
  },
};

function normalizeTemplateContent(raw: Record<string, unknown> | null | undefined): TemplateContent {
  const base = JSON.parse(JSON.stringify(DEFAULT_TEMPLATE_CONTENT)) as TemplateContent;
  if (!raw || typeof raw !== 'object') return base;

  return {
    ...base,
    ...raw,
    header: { ...base.header, ...(raw.header as object) },
    sections: raw.sections
      ? { hideZeroSections: true, ...(raw.sections as object) }
      : base.sections,
    customer: raw.customer
      ? { ...base.customer, ...(raw.customer as object) }
      : base.customer,
    table: raw.table ? { ...base.table, ...(raw.table as object) } : base.table,
    footer: { ...base.footer, ...(raw.footer as object) },
    colors: { ...base.colors, ...(raw.colors as object) },
  };
}

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  template,
  isOpen,
  onClose,
  onSave,
  buildingContextId,
  saveAsBuildingOverride = false,
  operatorScopeId = null,
}) => {
  const { t } = useLang();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateType: 'fee_notice',
    content: {} as TemplateContent,
  });
  const [activeTab, setActiveTab] = useState('fields');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || '',
        templateType: template.templateType,
        content: normalizeTemplateContent(template.content as Record<string, unknown>),
      });
    } else {
      resetForm();
    }
  }, [template]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      templateType: 'fee_notice',
      content: normalizeTemplateContent(null),
    });
  };

  const generatePreview = async () => {
    if (!template?.id) {
      toast.error(t('saveTemplateFirstForPreview'));
      return;
    }

    try {
      setIsLoadingPreview(true);
      const blob = await pdfTemplatesApi.previewTemplate(template.id);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error(t('failedGeneratePreview'));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSave = async () => {
    try {
      if (buildingContextId && saveAsBuildingOverride) {
        await pdfTemplatesApi.upsertBuildingDefault(buildingContextId, {
          templateType: formData.templateType,
          content: formData.content as unknown as Record<string, unknown>,
          name: formData.name,
          description: formData.description,
        });
        toast.success('Đã lưu mẫu cho chung cư');
      } else if (template) {
        await pdfTemplatesApi.updateTemplate(template.id, {
          ...formData,
          ...(operatorScopeId ? { operatorId: operatorScopeId } : {}),
        });
        toast.success(t('templateUpdatedSuccess'));
      } else {
        await pdfTemplatesApi.createTemplate({
          ...formData,
          ...(operatorScopeId ? { operatorId: operatorScopeId } : {}),
        });
        toast.success(t('templateCreatedSuccess'));
      }
      onSave();
    } catch (error: any) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.message || t('failedSaveTemplate'));
    }
  };

  const updateContent = (path: string, value: any) => {
    const pathArray = path.split('.');
    const newContent = JSON.parse(JSON.stringify(formData.content)) as TemplateContent;
    let current: Record<string, unknown> = newContent as unknown as Record<string, unknown>;

    for (let i = 0; i < pathArray.length - 1; i++) {
      const key = pathArray[i];
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key] as Record<string, unknown>;
    }
    current[pathArray[pathArray.length - 1]] = value;

    setFormData({ ...formData, content: newContent });
  };

  const isFeeNotice = formData.content.layout === 'fee_notice';
  const isReceipt = formData.content.layout === 'payment_receipt';
  const isReceiptDetailed = formData.content.layout === 'payment_receipt_detailed';
  const isIeIncomeReceipt = formData.content.layout === 'ie_income_receipt';
  const isOffsetVoucher = formData.content.layout === 'offset_voucher';
  const isSurplusVoucher = formData.content.layout === 'surplus_voucher';
  const isPdfBrandedLayout =
    isFeeNotice ||
    isReceipt ||
    isReceiptDetailed ||
    isIeIncomeReceipt ||
    isOffsetVoucher ||
    isSurplusVoucher;
  const isSpecialLayout = isPdfBrandedLayout;

  const addTableHeader = () => {
    const newHeaders = [...formData.content.table.headers, t('addColumn')];
    updateContent('table.headers', newHeaders);
  };

  const removeTableHeader = (index: number) => {
    const newHeaders = formData.content.table.headers.filter((_, i) => i !== index);
    updateContent('table.headers', newHeaders);
  };

  const updateTableHeader = (index: number, value: string) => {
    const newHeaders = [...formData.content.table.headers];
    newHeaders[index] = value;
    updateContent('table.headers', newHeaders);
  };

  const toggleCustomerField = (field: string) => {
    const currentFields = formData.content.customer.fields;
    const newFields = currentFields.includes(field)
      ? currentFields.filter(f => f !== field)
      : [...currentFields, field];
    updateContent('customer.fields', newFields);
  };

  const customerFieldLabels: Record<string, string> = {
    name: t('pdfCustomerFieldName'),
    address: t('pdfCustomerFieldAddress'),
    contract: t('pdfCustomerFieldContract'),
    taxCode: t('pdfCustomerFieldTaxCode'),
    phone: t('pdfCustomerFieldPhone'),
    email: t('pdfCustomerFieldEmail'),
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold">
            {template ? t('editPdfTemplate') : t('createNewPdfTemplate')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex min-h-0">
          {/* Left Panel - Form */}
          <div className="w-1/2 p-6 border-r overflow-y-auto max-h-full">
            {/* Basic Info */}
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-4">{t('basicInformation')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('templateName')}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {['fields', 'styling'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab === 'fields' ? t('fieldsAndLayout') : t('stylingAndColors')}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {activeTab === 'fields' && (
                <div className="space-y-6">
                  {/* Header Settings */}
                  <div>
                    <h4 className="text-md font-medium mb-3">{t('headerSettings')}</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.content.header?.showLogo ?? true}
                          onChange={(e) => updateContent('header.showLogo', e.target.checked)}
                          className="mr-2"
                        />
                        {t('showLogo')}
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.content.header?.showBuildingInfo ?? true}
                          onChange={(e) => updateContent('header.showBuildingInfo', e.target.checked)}
                          className="mr-2"
                        />
                        {t('showBuildingInfo')}
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.content.header?.bilingual ?? true}
                          onChange={(e) => updateContent('header.bilingual', e.target.checked)}
                          className="mr-2"
                        />
                        {t('bilingualHeader')}
                      </label>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('titleLabel')}
                        </label>
                        <input
                          type="text"
                          value={formData.content.header?.title || ''}
                          onChange={(e) => updateContent('header.title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>

                      {(isFeeNotice || isReceipt || isReceiptDetailed || isIeIncomeReceipt || isOffsetVoucher || isSurplusVoucher) && (
                        <>
                          {isFeeNotice && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('titleEnLabel')}
                              </label>
                              <input
                                type="text"
                                value={(formData.content.header as any)?.titleEn || ''}
                                onChange={(e) => updateContent('header.titleEn', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                              />
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('managementCompany')}
                            </label>
                            <input
                              type="text"
                              value={(formData.content.header as any)?.companyName || ''}
                              onChange={(e) => updateContent('header.companyName', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('managementCompanyEn')}
                            </label>
                            <input
                              type="text"
                              value={formData.content.header?.companyNameEn || ''}
                              onChange={(e) => updateContent('header.companyNameEn', e.target.value)}
                              placeholder={t('optionalLeaveBlank')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <LogoPathField
                              logoPath={(formData.content.header as any)?.logoPath || ''}
                              onLogoPathChange={(logoPath) => updateContent('header.logoPath', logoPath)}
                              label={t('logoPathOptional')}
                              pathHint={t('templateLogoPathHint')}
                              placeholder="backend/assets/images/company-logo.png"
                              onUpload={async (file) => {
                                const result = await buildingsApi.uploadBuildingLogoFile(
                                  file,
                                  operatorScopeId || undefined,
                                );
                                updateContent('header.logoPath', result.logoPath);
                              }}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('buildingLogoPathOptional')}
                            </label>
                            <input
                              type="text"
                              value={(formData.content.header as any)?.buildingLogoPath || ''}
                              onChange={(e) => updateContent('header.buildingLogoPath', e.target.value)}
                              placeholder={t('templateBuildingLogoPathHint')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              {t('templateBuildingLogoPathHint')}
                            </p>
                          </div>
                          {(isFeeNotice || isReceiptDetailed) && (
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={(formData.content as any).sections?.hideZeroSections !== false}
                              onChange={(e) => updateContent('sections.hideZeroSections', e.target.checked)}
                              className="mr-2"
                            />
                            {t('hideZeroSections')}
                          </label>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Customer Information - not used by fee_notice / payment_receipt layout */}
                  {!isSpecialLayout && (
                  <div>
                    <h4 className="text-md font-medium mb-3">{t('customerInformation')}</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.content.customer.showBox}
                          onChange={(e) => updateContent('customer.showBox', e.target.checked)}
                          className="mr-2"
                        />
                        {t('showCustomerInfoBox')}
                      </label>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('customerFields')}
                        </label>
                        <div className="space-y-2">
                          {['name', 'address', 'contract', 'taxCode', 'phone', 'email'].map((field) => (
                            <label key={field} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.content.customer.fields.includes(field)}
                                onChange={() => toggleCustomerField(field)}
                                className="mr-2"
                              />
                              {customerFieldLabels[field] || field}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Table Settings - not used by fee_notice / payment_receipt layout */}
                  {!isSpecialLayout && (
                  <div>
                    <h4 className="text-md font-medium mb-3">{t('tableSettings')}</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.content.table.showBorders}
                          onChange={(e) => updateContent('table.showBorders', e.target.checked)}
                          className="mr-2"
                        />
                        {t('showBorders')}
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.content.table.alternateRowColors}
                          onChange={(e) => updateContent('table.alternateRowColors', e.target.checked)}
                          className="mr-2"
                        />
                        {t('alternateRowColors')}
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.content.table.showVAT}
                          onChange={(e) => updateContent('table.showVAT', e.target.checked)}
                          className="mr-2"
                        />
                        {t('showVatColumns')}
                      </label>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('tableHeaders')}
                        </label>
                        <div className="space-y-2">
                          {formData.content.table.headers.map((header, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={header}
                                onChange={(e) => updateTableHeader(index, e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <button
                                onClick={() => removeTableHeader(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={addTableHeader}
                            className="text-blue-500 hover:text-blue-700 text-sm"
                          >
                            + {t('addColumn')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Footer Settings */}
                  <div>
                    <h4 className="text-md font-medium mb-3">{t('footerSettings')}</h4>
                    <div className="space-y-3">
                      {!isReceipt && !isReceiptDetailed && [
                        { key: 'showPaymentInfo', label: t('showPaymentInfo') },
                        { key: 'showBankInfo', label: t('showBankInfo') },
                        ...(!isFeeNotice ? [{ key: 'showSignature', label: t('showSignatureFields') }] : []),
                        {
                          key: 'showQRCode',
                          label: isFeeNotice ? t('showPaymentQr') : t('showPaymentQr'),
                        },
                        ...(isFeeNotice
                          ? [{ key: 'showAppQrCode', label: t('showAppQr') }]
                          : []),
                        { key: 'showNotice', label: t('showNoticeText') },
                      ].map(({ key, label }) => (
                        <label key={key} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={
                              formData.content.footer?.[
                                key as
                                  | 'showPaymentInfo'
                                  | 'showBankInfo'
                                  | 'showSignature'
                                  | 'showQRCode'
                                  | 'showAppQrCode'
                                  | 'showNotice'
                              ] ??
                              (key === 'showAppQrCode' && isFeeNotice ? true : false)
                            }
                            onChange={(e) => updateContent(`footer.${key}`, e.target.checked)}
                            className="mr-2"
                          />
                          {label}
                        </label>
                      ))}
                      {(isReceipt || isReceiptDetailed) && (
                        <>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.content.footer?.showSignatures !== false}
                              onChange={(e) => updateContent('footer.showSignatures', e.target.checked)}
                              className="mr-2"
                            />
                            {t('showSignatureBlocks')}
                          </label>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('defaultReason')}
                            </label>
                            <input
                              type="text"
                              value={formData.content.footer?.defaultReason || 'Thu phí'}
                              onChange={(e) => updateContent('footer.defaultReason', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                        </>
                      )}
                      {isFeeNotice && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('hotlineLabel')}</label>
                            <input
                              type="text"
                              value={formData.content.footer?.hotline || ''}
                              onChange={(e) => updateContent('footer.hotline', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('officeHoursVi')}</label>
                            <input
                              type="text"
                              value={formData.content.footer?.officeHours || ''}
                              onChange={(e) => updateContent('footer.officeHours', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('noticeTextVi')}</label>
                            <FeeNoticeRichTextarea
                              value={formData.content.footer?.noticeText || ''}
                              onChange={(v) => updateContent('footer.noticeText', v)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('noticeTextEn')}</label>
                            <FeeNoticeRichTextarea
                              value={formData.content.footer?.noticeTextEn || ''}
                              onChange={(v) => updateContent('footer.noticeTextEn', v)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'styling' && (
                <div className="space-y-6">
                  {/* Layout */}
                  <div>
                    <h4 className="text-md font-medium mb-3">{t('layoutLabel')}</h4>
                    <select
                      value={formData.content.layout}
                      onChange={(e) => updateContent('layout', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="fee_notice">{t('layoutFeeNotice')}</option>
                      <option value="payment_receipt">{t('layoutPaymentReceipt')}</option>
                      <option value="payment_receipt_detailed">{t('layoutPaymentReceiptDetailed')}</option>
                      <option value="professional">{t('layoutProfessional')}</option>
                      <option value="simple">{t('layoutSimple')}</option>
                      <option value="custom">{t('layoutCustom')}</option>
                    </select>
                  </div>

                  {/* Colors */}
                  <div>
                    <h4 className="text-md font-medium mb-3">{t('stylingAndColors')}</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('primaryColor')}
                        </label>
                        <input
                          type="color"
                          value={formData.content.colors.primary}
                          onChange={(e) => updateContent('colors.primary', e.target.value)}
                          className="w-full h-10 border border-gray-300 rounded"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('secondaryColor')}
                        </label>
                        <input
                          type="color"
                          value={formData.content.colors.secondary}
                          onChange={(e) => updateContent('colors.secondary', e.target.value)}
                          className="w-full h-10 border border-gray-300 rounded"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('accentColor')}
                        </label>
                        <input
                          type="color"
                          value={formData.content.colors.accent}
                          onChange={(e) => updateContent('colors.accent', e.target.value)}
                          className="w-full h-10 border border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="w-1/2 p-6 bg-gray-50 flex flex-col max-h-full">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 className="text-lg font-medium">{t('previewLabel')}</h3>
              <button
                onClick={generatePreview}
                disabled={isLoadingPreview || !template?.id}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {isLoadingPreview ? t('generatingPreview') : t('generatePreview')}
              </button>
            </div>
            
            <div className="flex-1 border border-gray-300 rounded-lg bg-white min-h-0">
              {previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full rounded-lg"
                  title={t('previewLabel')}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 p-4 text-center">
                  {template?.id 
                    ? t('clickGeneratePreview')
                    : t('saveTemplateFirstPreview')
                  }
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t bg-white flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            {template ? t('updateTemplate') : t('createTemplate')}
          </button>
        </div>
      </div>
    </div>
  );
}; 